import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Drawer,
  Button,
  Divider,
  CardContent,
} from "@mui/material";

import {
  getParentClientSearch,
  getParentClientDetails,
  getRosterMilestoneSearchResults,
  getAllMilestones,
  saveClassRosterMilestones,
  getTraineeMilestoneAssociate,
  deleteMilestoneAssociate,
} from "../../services/userServices";
import BackdropComponent from "../Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import { visuallyHidden } from "@mui/utils";
import DisplayProjects from "components/DisplayProjects/DisplayProjects";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";

const createData = (sourceData, MileStoneList) => {
  return sourceData?.map((item) => {
    const rosterMilestones = item?.milestones?.split("|");

    const milestonesData = MileStoneList?.map((milestone) => {
      return {
        [milestone?.Milestone]:
          rosterMilestones === undefined
            ? false
            : rosterMilestones?.includes(milestone?.Milestone),
      };
    });

    const milestonesDetails = Object.assign({}, ...milestonesData);

    return {
      rosterClassId: `${item?.classRosterId} - ${item?.Wave}`,
      rosterId: item?.classRosterId,
      wave: item?.wave,
      ...milestonesDetails,
    };
  });
};
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}
const TableSortLabelStyle = {
  "&.MuiTableSortLabel-root": {
    color: "black",
    fontWeight: "bold",
  },
  "&.Mui-active": {
    color: "black",
  },
  "& .MuiTableSortLabel-icon": {
    color: "#8c8c8c !important",
  },
};
const disabledCheckBoxStyle = {
  "&.Mui-disabled": {
    pointerEvents: "auto",

    "& .MuiSvgIcon-root": {
      color: "#8c8c8c",
    },
  },
  "&.Mui-checked": {
    color: "#3299FF",
  },
};
const headCheckBoxStyle = {
  "&.Mui-checked": {
    color: "#3299FF",
  },
};
function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    onRequestSort,
    headCells,
    rowCount,
    headCheckBoxStatuses,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells?.length > 0 && (
          <TableCell
            key={"rosterClassId"}
            //align={headCells[0].numeric ? 'right' : 'left'}
            align="center"
            padding="none"
            sortDirection={orderBy === "rosterClassId" ? order : false}
            sx={{
              width: 1,
              fontWeight: "bold",
              fontFamily: "Urbanist, sans-serif",
            }}
          >
            <TableSortLabel
              sx={{
                width: "10rem",
                color: "#3F5765",
                fontWeight: "bold",
                fontFamily: "Urbanist, sans-serif",
                ...TableSortLabelStyle,
              }}
              active={orderBy === "rosterClassId"}
              direction={orderBy === "rosterClassId" ? order : "asc"}
              onClick={createSortHandler("rosterClassId")}
            >
              {"Roster ID-Class name"}
              {orderBy === "rosterClassId" ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        )}

        {headCells?.map((headCell) => {
          return (
            <TableCell
              align="center"
              padding="checkbox"
              key={headCell?.MilestoneId}
              sx={{
                fontWeight: "bold",
                fontFamily: "Urbanist, sans-serif",
                paddingTop: 10,
                color: "#3F5765",
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  {headCell?.Milestone}
                </Grid>
                <Grid item xs={12}>
                  <Checkbox
                    color="primary"
                    id={headCell.Milestone}
                    onChange={onSelectAllClick}
                    checked={headCheckBoxStatuses[headCell?.Milestone] === true}
                    sx={headCheckBoxStyle}
                    disabled={rowCount === 0}
                  />
                </Grid>
              </Grid>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  headCheckBoxStatuses: PropTypes.object.isRequired,
  headCells: PropTypes.array.isRequired,
};
const prepareHeadCheckBoxStatuses = (milestones) => {
  const statuses = milestones.map((milestone) => {
    return { [milestone?.Milestone]: false };
  });

  return Object.assign({}, ...statuses);
};

function ViewProjects(props) {
  const { projectNames, searchBy, handleViewProjects } = props;
  return projectNames?.length === 1 ? (
    <Grid container>
      <Grid
        item
        xs={searchBy === "parentClient" ? 2.5 : 1.5}
        sx={{
          marginTop: "39px",
        }}
      >
        <Typography
          variant={searchBy === "parentClient" ? "subtitle1" : "h6"}
          sx={{
            color: "#3F5765",
            fontFamily: "Urbanist, sans-serif",
            fontWeight: "bold",
          }}
        >
          Project:
        </Typography>
      </Grid>
      <Grid
        item
        xs={searchBy === "parentClient" ? 9.5 : 10.5}
        sx={{
          marginTop: "39px",
        }}
      >
        <Typography
          variant={searchBy === "parentClient" ? "subtitle1" : "h6"}
          sx={{ color: "#3299FF", fontWeight: "bold" }}
        >
          {projectNames}
        </Typography>
      </Grid>
    </Grid>
  ) : (
    <Button
      sx={{
        textTransform: "none",
        borderRadius: "7px",
        backgroundColor: "#3299FF",
        marginTop: "38px",
        width: "176px",
        fontFamily: "Urbanist, sans-serif",
        marginLeft: "25px",
      }}
      variant="contained"
      onClick={handleViewProjects}
    >
      View Projects
    </Button>
  );
}
ViewProjects.propTypes = {
  projectNames: PropTypes.array.isRequired,
  searchBy: PropTypes.string.isRequired,
  handleViewProjects: PropTypes.func.isRequired,
};

function WarningDialog({ open, warnings, onConfirm, onCancel }) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "20px",
        },
      }}
    >
      <DialogContent>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Box
            sx={{
              width: "60px",
              height: "60px",

              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningAmberIcon
              sx={{
                fontSize: "35px",
                color: "#FF6F00",
              }}
            />
          </Box>
        </Box>

        <Typography
          sx={{
            textAlign: "center",
            fontFamily: "Urbanist, sans-serif",
            fontWeight: "bold",
            fontSize: "20px",
            color: "#FF6F00",
            mb: 1,
          }}
        >
          Disassociate Milestone
        </Typography>

        <Box
          sx={{
            backgroundColor: "#FFF8F0",
            borderRadius: "8px",
            padding: "16px",
            mb: 2,
          }}
        >
          {warnings.map((warning, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                mb: index !== warnings.length - 1 ? 1.5 : 0,
              }}
            >
              <Typography
                sx={{
                  color: "#E65100",
                  fontWeight: "bold",
                  fontSize: "16px",
                  mt: "2px",
                }}
              >
                <CloseIcon
                  sx={{
                    color: "#E65100",
                    fontSize: "18px",
                    mt: "2px",
                    flexShrink: 0,
                  }}
                />
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Urbanist, sans-serif",
                  fontSize: "14px",
                  color: "#3F5765",
                }}
              >
                {warning}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography
          sx={{
            textAlign: "center",
            fontFamily: "Urbanist, sans-serif",
            fontSize: "13px",
            color: "#9E9E9E",
            fontStyle: "italic",
            mb: 2,
          }}
        >
          This action cannot be undone. Are you sure you want to continue?
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: "16px" }}>
          <Button
            onClick={onCancel}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              borderColor: "#3299FF",
              color: "#3299FF",
              fontFamily: "Urbanist, sans-serif",
              textTransform: "none",
              width: "120px",
              "&:hover": { borderColor: "#9E9E9E" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            sx={{
              borderRadius: "8px",
              backgroundColor: "#3299FF",
              fontFamily: "Urbanist, sans-serif",
              textTransform: "none",
              width: "120px",
              "&:hover": { backgroundColor: "#1a8cff" },
            }}
          >
            Continue
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function MileStoneRosterAssociation() {
  const searchBySource = [
    { displayValue: "Class Request", key: "classRequest" },
    { displayValue: "Class Roster", key: "classRoster" },
    { displayValue: "Parent client", key: "parentClient" },
  ];

  const [searchBy, setSearchBy] = useState("classRequest");
  const [milestonesList, setMilestonesList] = useState([]);
  const [parentClientsList, setParentClientsList] = useState([]);
  const [selectedParentClient, setSelectedParentClient] = useState("");
  const [classRequestIds, setClassRequestIds] = useState([]);
  const [selectedClassRequest, setSelectedClassRequest] = useState("");
  const searchValueRef = useRef("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("rosterClassId");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const dense = true;
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState([]);
  const [rosterMileStonesData, setRosterMileStonesData] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [classRequestValue, setClassRequestValue] = useState({
    label: "",
    classRequestId: "",
  });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [warningDialog, setWarningDialog] = useState({
    open: false,
    warnings: [],
    pendingAction: null,
    milestoneName: null,
  });

  const rosterMileStonesDataRef = useRef([]);

  useEffect(() => {
    rosterMileStonesDataRef.current = rosterMileStonesData;
  }, [rosterMileStonesData]);

  const currentUser = localStorage.getItem("User");
  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const [headCheckBoxStatuses, setHeadCheckBoxStatuses] = useState({});
  const [projectNames, setProjectNames] = useState([]);

  useEffect(() => {
    let cancelled = false;

    localStorage.setItem("MileStones", window.location.pathname);

    const fetchData = async () => {
      try {
        if (!cancelled) {
          setLoading(true);
        }

        const response = await getAllMilestones();

        if (cancelled) return;

        if (response?.status === true) {
          if (!cancelled) {
            setMilestonesList(response?.data);
            setHeadCheckBoxStatuses(
              prepareHeadCheckBoxStatuses(response?.data),
            );
          }
        } else {
          if (!cancelled) {
            setSnack({
              open: true,
              message: `Some internal error occured`,
              severity: "error",
            });
          }
        }
      } catch (error) {
        if (!cancelled) {
          setSnack({
            open: true,
            message: `Some internal error occured!`,
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSearchByChange = async (e) => {
    setLoading(true);
    setSearchBy(e.target.value);
    resetFields();
    if (e.target.value === "parentClient") {
      const parentClientDetails = await getParentClientSearch();
      if (parentClientDetails) {
        setParentClientsList(parentClientDetails);
        window.localStorage.removeItem("MileStones");
      }
    }
    setLoading(false);
  };

  const handleClassRequestChange = (e, value) => {
    setClassRequestValue(value);
    setSelectedClassRequest(value?.classRequestId);
  };

  const handleParentClientChange = async (event, value) => {
    setLoading(true);
    setClassRequestValue({
      label: "",
      classRequestId: "",
    });
    setSelectedParentClient(value?.parentClientCode);
    let data = {
      parentClientCode: value?.parentClientCode,
    };

    const parentClientDetails = await getParentClientDetails(data);
    if (parentClientDetails?.status === true) {
      const classRequestsData = parentClientDetails?.data?.clientDetails?.map(
        (item) => {
          return {
            label: `${item?.project} - ${item?.wave} - ${item?.classRequestId}`,
            classRequestId: item?.classRequestId,
          };
        },
      );
      setClassRequestIds(classRequestsData);
    }
    setLoading(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchValueRef?.current?.value === "") {
      showWarning("Please enter a " + searchBy + "id");
    } else if (
      searchBy === "parentClient" &&
      (!selectedParentClient || !selectedClassRequest)
    ) {
      showWarning("please select a " + getSelectionMessage());
    } else {
      setLoading(true);

      try {
        setHeadCheckBoxStatuses(prepareHeadCheckBoxStatuses(milestonesList));
        await searchRosterMilestones();
      } catch (error) {
        showError("Some internal error occured!");
      }
      setPage(0);
      setLoading(false);
    }
  };
  const showWarning = (message) => {
    setSnack({ open: true, message, severity: "warning" });
  };

  const showError = (message) => {
    setSnack({ open: true, message, severity: "error" });
  };

  const searchRosterMilestones = async () => {
    const searchByParentClient =
      searchBy === "parentClient" ? selectedClassRequest : 0;
    const response = await getRosterMilestoneSearchResults(
      searchBy === "classRequest"
        ? searchValueRef.current.value
        : searchByParentClient,
      searchBy === "classRoster" ? searchValueRef.current.value : 0,
    );
    if (response?.status === true) {
      const data = response?.data;
      setProjectNames(data?.projectNames?.split("|"));
      setRows(createData(data?.rosterMilestoneDetails, milestonesList));
      setRosterMileStonesData(
        createData(data?.rosterMilestoneDetails, milestonesList),
      );
      window.localStorage.removeItem("MileStones");
    } else if (response?.statusCode === 404) {
      showWarning("No data found for the search request.");
      resetFields();
    }
  };

  const getSelectionMessage = () => {
    const classRequestCheck =
      selectedClassRequest === null
        ? "a valid class request "
        : "Class request";
    return selectedParentClient === "" ? "parentClient " : classRequestCheck;
  };

  const resetFields = () => {
    setClassRequestIds([]);
    setClassRequestValue({
      label: "",
      classRequestId: "",
    });
    setSelected([]);
    setPage(0);
    setHeadCheckBoxStatuses(prepareHeadCheckBoxStatuses(milestonesList));
    setRows([]);
    setProjectNames([]);
    if (searchValueRef.current !== null) {
      searchValueRef.current.value = "";
    }
  };

  const handleReset = () => {
    resetFields();
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleAddOrRemove = (currentState, value) => {
    const source = currentState;
    if (!source?.includes(value)) {
      source?.push(value);
    } else {
      source?.splice(source?.indexOf(value), 1);
    }
    return source;
  };

  const isEditable = (rosterId, milestone) => {
    const rosterMileStone = rosterMileStonesData?.find(
      (item) => item?.rosterId === rosterId,
    );

    return rosterMileStone === undefined ? true : !rosterMileStone[milestone];
  };

  const closeWarningDialog = () => {
    setWarningDialog({
      open: false,
      warnings: [],
      pendingAction: null,
      milestoneName: null,
    });
  };

  const handleWarningConfirm = () => {
    if (warningDialog.pendingAction) {
      warningDialog.pendingAction();
    }
  };

  const handleWarningCancel = () => {
    if (warningDialog.milestoneName) {
      setHeadCheckBoxStatuses((prev) => ({
        ...prev,
        [warningDialog.milestoneName]: true,
      }));
    }
    closeWarningDialog();
  };

  const performSelectAll = (event) => {
    let source = rows;

    let selecteditems = selected;
    const data = source?.map((item) => {
      selecteditems = handleAddOrRemove(
        selecteditems,
        `${item?.rosterId}-${event.target.id}`,
      );

      return {
        ...item,
        [event.target.id]: isEditable(item?.rosterId, event.target.id)
          ? event.target.checked
          : item[event.target.id],
      };
    });
    setHeadCheckBoxStatuses({
      ...headCheckBoxStatuses,
      [event.target.id]: event.target.checked,
    });
    setRows(data);
    setSelected(selecteditems);
  };

  const updateRosterMileStonesData = (rosterId, milestoneName, isChecked) => {
    setRosterMileStonesData((prev) =>
      prev.map((item) => {
        if (item?.rosterId === rosterId) {
          return {
            ...item,
            [milestoneName]: isChecked,
          };
        }
        return item;
      }),
    );
  };

  const executeDelete = async (
    rosterId,
    milestoneId,
    milestoneName,
    isChecked,
  ) => {
    setLoading(true);
    const response = await deleteMilestoneAssociate(rosterId, milestoneId);

    if (response?.statusCode === 200) {
      updateRowCheckState(rosterId, milestoneName, isChecked);
      setSnack({
        open: true,
        message: "Milestone disassociated successfully.",
        severity: "success",
      });
    } else {
      setSnack({
        open: true,
        message: "Failed to disassociate milestone.",
        severity: "error",
      });
    }
    setLoading(false);
    closeWarningDialog();
  };

  const executeBulkDelete = async (affectedRosters, milestoneName) => {
    setLoading(true);
    const milestoneObj = milestonesList?.find(
      (m) => m?.Milestone === milestoneName,
    );

    const deletePromises = affectedRosters.map((row) =>
      deleteMilestoneAssociate(row?.rosterId, milestoneObj?.MilestoneId),
    );

    const results = await Promise.all(deletePromises);
    const allSuccess = results.every((r) => r?.statusCode === 200);

    if (allSuccess) {
      affectedRosters.forEach((row) => {
        updateRowCheckState(row?.rosterId, milestoneName, false);
      });

      setHeadCheckBoxStatuses((prev) => ({
        ...prev,
        [milestoneName]: false,
      }));

      setSnack({
        open: true,
        message: "Milestones disassociated successfully.",
        severity: "success",
      });
    } else {
      setSnack({
        open: true,
        message: "Some milestones failed to disassociate.",
        severity: "error",
      });
    }
    setLoading(false);
    closeWarningDialog();
  };

  const handleSelectAllClick = async (event) => {
    const isUnchecking = !event.target.checked;
    const milestoneName = event.target.id;

    if (isUnchecking) {
      const affectedRosters = rows?.filter((row) =>
        isAlreadySaved(row?.rosterId, milestoneName),
      );

      if (affectedRosters?.length > 0) {
        setLoading(true);
        const milestoneObj = milestonesList?.find(
          (m) => m?.Milestone === milestoneName,
        );

        const checkPromises = affectedRosters.map((row) =>
          getTraineeMilestoneAssociate(
            row?.rosterId,
            milestoneObj?.MilestoneId,
          ),
        );

        const results = await Promise.all(checkPromises);

        const rosterWithData = affectedRosters.filter(
          (_, index) => results[index]?.data > 0,
        );

        if (rosterWithData?.length > 0) {
          setLoading(false);
          const warnings = rosterWithData.map(
            (row) =>
              `WARNING: There are already statuses recorded for Roster ID ${row?.rosterId} & Milestone ${milestoneName}. Disassociating this Milestone will cause those statuses to be deleted.`,
          );
          setWarningDialog({
            open: true,
            warnings,
            pendingAction: () =>
              executeBulkDelete(affectedRosters, milestoneName),
            milestoneName: milestoneName,
          });
          return;
        }

        const deletePromises = affectedRosters.map((row) =>
          deleteMilestoneAssociate(row?.rosterId, milestoneObj?.MilestoneId),
        );

        const deleteResults = await Promise.all(deletePromises);
        const allSuccess = deleteResults.every((r) => r?.statusCode === 200);

        setLoading(false);

        if (allSuccess) {
          affectedRosters.forEach((row) => {
            updateRowCheckState(row?.rosterId, milestoneName, false);
          });
          setHeadCheckBoxStatuses((prev) => ({
            ...prev,
            [milestoneName]: false,
          }));
          setSnack({
            open: true,
            message: "Milestones disassociated successfully.",
            severity: "success",
          });
        } else {
          setSnack({
            open: true,
            message: "Some milestones failed to disassociate.",
            severity: "error",
          });
        }
        return;
      }

      rows.forEach((row) => {
        updateRowCheckState(row?.rosterId, milestoneName, false);
      });
      setHeadCheckBoxStatuses((prev) => ({
        ...prev,
        [milestoneName]: false,
      }));
      return;
    }

    performSelectAll(event);
  };
  const updateRowCheckState = (rosterId, milestoneName, isChecked) => {
    setRows((prevRows) =>
      prevRows.map((item) => {
        if (item?.rosterId === rosterId) {
          return {
            ...item,
            [milestoneName]: isChecked,
          };
        }
        return item;
      }),
    );
    updateRosterMileStonesData(rosterId, milestoneName, isChecked);

    const key = `${rosterId}-${milestoneName}`;
    setSelected((prevSelected) => {
      if (isChecked) {
        return prevSelected.includes(key)
          ? prevSelected
          : [...prevSelected, key];
      } else {
        return prevSelected.filter((item) => item !== key);
      }
    });
  };

  const isAlreadySaved = (rosterId, milestone) => {
    const rosterMileStone = rosterMileStonesDataRef.current?.find(
      (item) => item?.rosterId === rosterId,
    );
    return rosterMileStone !== undefined && rosterMileStone[milestone] === true;
  };

  const handleSelect = async (event, rosterId) => {
    const isChecked = event.target.checked;
    const milestoneName = event.target.id;
    const isUnchecking = !isChecked;

    if (isUnchecking && isAlreadySaved(rosterId, milestoneName)) {
      setLoading(true);

      const milestoneObj = milestonesList?.find(
        (m) => m?.Milestone === milestoneName,
      );

      try {
        const response = await getTraineeMilestoneAssociate(
          rosterId,
          milestoneObj?.MilestoneId,
        );

        if (response?.statusCode === 200 && response?.data > 0) {
          setLoading(false);
          setWarningDialog({
            open: true,
            warnings: [
              `WARNING: There are already statuses recorded for Roster ID ${rosterId} & Milestone ${milestoneName}. Disassociating this Milestone will cause those statuses to be deleted.`,
            ],
            pendingAction: () =>
              executeDelete(
                rosterId,
                milestoneObj?.MilestoneId,
                milestoneName,
                isChecked,
              ),
          });
          return;
        }

        const deleteResponse = await deleteMilestoneAssociate(
          rosterId,
          milestoneObj?.MilestoneId,
        );

        setLoading(false);

        if (deleteResponse?.statusCode === 200) {
          updateRowCheckState(rosterId, milestoneName, isChecked);
          setSnack({
            open: true,
            message: "Milestone disassociated successfully.",
            severity: "success",
          });
        } else {
          updateRowCheckState(rosterId, milestoneName, true);
          setSnack({
            open: true,
            message: "Failed to disassociate milestone.",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error in handleSelect:", error);
        setLoading(false);
        setSnack({
          open: true,
          message: "Some internal error occurred!",
          severity: "error",
        });
      }
      return;
    }

    updateRowCheckState(rosterId, milestoneName, isChecked);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [rows, order, orderBy, page, rowsPerPage],
  );

  const handleSave = async () => {
    if (selected?.length > 0) {
      const payload = prepareSavePayload();

      setLoading(true);
      const response = await saveClassRosterMilestones(payload);

      if (response?.message === "Success") {
        setRosterMileStonesData(rows);
        setHeadCheckBoxStatuses(prepareHeadCheckBoxStatuses(milestonesList));
        setLoading(false);
        setSnack({
          open: true,
          message: "Success, Milestones associated to rosters.",
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message: "Save failed, some internal error occured.",
          severity: "error",
        });
      }
    } else {
      setSnack({
        open: true,
        message: "No changes to save, please check milestones.",
        severity: "warning",
      });
    }
  };

  const handleViewProjects = () => {
    setPanelOpen(!panelOpen);
  };

  const getRosterId = (value) => {
    return value?.split("-")[0];
  };

  const getMilestoneId = (value) => {
    const mileStone = value?.split("-")[1];
    const mileStoneObject = milestonesList?.find(
      (m) => m?.Milestone === mileStone,
    );
    return mileStoneObject.MilestoneId;
  };

  const prepareSavePayload = () => {
    const classRosterMileStones = selected?.map((item) => ({
      classRosterId: getRosterId(item),
      milestoneId: getMilestoneId(item),
      createdBy: currentUser,
    }));

    return classRosterMileStones;
  };

  return (
    <div>
      {loading ? <BackdropComponent /> : null}
      <WarningDialog
        open={warningDialog.open}
        warnings={warningDialog.warnings}
        onConfirm={handleWarningConfirm}
        onCancel={handleWarningCancel}
      ></WarningDialog>
      <CardContent>
        <Grid xs={12}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "20px",
              fontFamily: "Urbanist, sans-serif",
              color: "#3F5765",
              fontWeight: "bold",
              mt: "10px",
              mb: "10px",
            }}
          >
            Filter View
          </Typography>
        </Grid>
        <Grid
          container
          direction="row"
          sx={{
            justifyContent: "flex-start",
            alignItems: "stretch",
          }}
        >
          <Grid item xs={1.8}>
            <FormControl>
              <Typography
                sx={{
                  marginTop: "10px",
                  fontSize: "14px",
                  fontFamily: "Urbanist, sans-serif",
                  color: "#3F5765",
                  fontWeight: "bold",
                }}
              >
                Search by
              </Typography>

              <Select
                sx={{
                  marginTop: "7px",
                  width: "156px",
                  height: "39px",
                  radius: "8px",
                  color: "#747474",
                }}
                value={searchBy}
                onChange={handleSearchByChange}
              >
                {searchBySource?.map((item) => (
                  <MenuItem key={item?.key} value={item?.key}>
                    {" "}
                    {`${item?.displayValue}`}{" "}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={searchBy !== "ParentClient" ? 2.3 : 2.1} item>
            {searchBy !== "parentClient" ? (
              <TextField
                fullWidth
                size="small"
                id="outlined-basic"
                variant="outlined"
                sx={{
                  marginTop: "36px",
                  radius: "8px",
                  color: "#747474",
                }}
                placeholder="Search value"
                inputRef={searchValueRef}
                InputProps={{
                  sx: {
                    color: "#3f5765",
                  },
                }}
              />
            ) : (
              <Autocomplete
                sx={{
                  marginTop: "37px",
                  width: "206px",
                  height: "39px",
                  radius: "8px",
                }}
                disablePortal
                id="combo-box-demo"
                options={parentClientsList}
                getOptionLabel={(item) =>
                  `${item?.parentClientName} - ${item?.parentClientCode}`
                }
                onChange={(event, value) =>
                  handleParentClientChange(event, value)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Parent client"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        color: "#3f5765",
                      },
                    }}
                  />
                )}
              />
            )}
          </Grid>
          {classRequestIds?.length > 0 && (
            <Grid item xs={1.8}>
              <Autocomplete
                sx={{
                  marginTop: "37px",
                  width: "235px",
                  height: "39px",
                  radius: "8px",
                  color: "#747474",
                }}
                disablePortal
                id="combo-box-demo"
                value={classRequestValue}
                options={classRequestIds}
                getOptionLabel={(item) =>
                  item?.label === "" && item?.classRequestId === ""
                    ? ""
                    : item?.label?.toString()
                }
                onChange={(event, value) =>
                  handleClassRequestChange(event, value)
                }
                renderInput={(params) => (
                  <TextField
                    sx={{
                      color: "#747474",
                    }}
                    {...params}
                    size="small"
                    label="Project-Wave-Class request"
                  />
                )}
              />
            </Grid>
          )}
          <Grid
            xs={1.1}
            item
            sx={{
              marginLeft: "20px",
            }}
          >
            <Button
              sx={{
                textTransform: "none",
                borderRadius: "7px",
                backgroundColor: "#3299FF",
                marginTop: "38px",
                width: "76px",
                fontFamily: "Urbanist, sans-serif",
                marginLeft: "25px",
              }}
              variant="contained"
              onClick={handleSearch}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={0.8}>
            <Button
              sx={{
                textTransform: "none",
                borderRadius: "7px",
                borderColor: "#1a8cff",
                marginTop: "38px",
              }}
              variant="outlined"
              onClick={handleReset}
            >
              Reset
            </Button>
          </Grid>

          <Grid item xs={1}>
            <Button
              sx={{
                textTransform: "none",
                borderRadius: "7px",
                borderColor: "#1a8cff",
                marginTop: "38px",
              }}
              variant="outlined"
              onClick={handleSave}
            >
              Save
            </Button>
          </Grid>
          {projectNames?.length > 0 ? (
            <Grid item xs={classRequestIds?.length > 0 ? 2.3 : 4.8}>
              <ViewProjects
                projectNames={projectNames}
                searchBy={
                  classRequestIds?.length > 0 ? "parentClient" : "class"
                }
                handleViewProjects={handleViewProjects}
              />
            </Grid>
          ) : (
            <Grid xs={classRequestIds?.length > 0 ? 2.3 : 4.8} item></Grid>
          )}
          <Grid
            item
            xs={12}
            sx={{
              marginTop: "30px",
              marginBottom: "10px",
            }}
          >
            <Divider
              sx={{
                flexGrow: 1,
                ml: 3,
                borderBottomWidth: 2,
              }}
            ></Divider>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              marginTop: "10px",
            }}
          >
            <TableContainer
              sx={{
                maxHeight: "calc(100vh - 280px)",
                overflow: "auto",
              }}
            >
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby="tableTitle"
                size={dense ? "small" : "medium"}
                stickyHeader
              >
                <EnhancedTableHead
                  numSelected={selected?.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={rows?.length}
                  headCells={milestonesList}
                  headCheckBoxStatuses={headCheckBoxStatuses}
                />
                <TableBody>
                  {visibleRows.map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow hover tabIndex={-1} key={row?.rosterId}>
                        <TableCell
                          sx={{
                            marginBottom: "10px",
                            color: "#3F5765",
                            width: 1,
                          }}
                          id={labelId}
                          scope="row"
                          align="center"
                          size="medium"
                        >
                          <Grid container>
                            <Grid item xs={12}>
                              {" "}
                              {`Roster Id : ${row?.rosterId}`}{" "}
                            </Grid>
                            <Grid item xs={12}>
                              {" "}
                              {`Class name: ${row?.wave}`}{" "}
                            </Grid>
                          </Grid>
                        </TableCell>
                        {milestonesList?.map((milestone) => {
                          return (
                            <TableCell
                              align="center"
                              key={milestone?.MilestoneId}
                            >
                              <Checkbox
                                color="primary"
                                id={milestone?.Milestone}
                                onChange={(event) =>
                                  handleSelect(event, row?.rosterId)
                                }
                                checked={row[milestone?.Milestone]}
                                sx={{
                                  "&.Mui-checked": {
                                    color: "#3299FF",
                                  },
                                }}
                                inputProps={{
                                  "aria-labelledby": labelId,
                                }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow
                      sx={{
                        height: (dense ? 33 : 53) * emptyRows,
                      }}
                    >
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={rows.length}
              rowsPerPage={10}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Grid>
        </Grid>
      </CardContent>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
      <Drawer
        anchor={"right"}
        open={panelOpen}
        onClose={handleViewProjects}
        PaperProps={{
          sx: { width: "40%" },
        }}
      >
        <DisplayProjects projectNames={projectNames}></DisplayProjects>
      </Drawer>
    </div>
  );
}
export default MileStoneRosterAssociation;
